import { mergeClassNames } from '@/utils/mergeClassNames';
import { Icon, Text } from '@zapier/design-system';
import { useRef } from 'react';
import { useButton } from 'react-aria';
import type { PopoverProps as ReactAriaPopoverProps } from 'react-aria-components';
import { Dialog, Popover as ReactAriaPopover } from 'react-aria-components';
import styles from './Popover.module.scss';

export type PopoverProps = Omit<ReactAriaPopoverProps, 'children'> & {
  /** The color of the popover background.  Use 'dark' when overlayed on a light-colored element. */
  color?: 'dark' | 'light';
  /** The title to render at the top of the popover. */
  title?: string;
  /** The content of the popover.
   *
   * Also accepts a renderProp function that takes an arg containing the following:
   *   trigger: The name of the component that triggered the popver.
   *   placement: The placement of the popover relative to the trigger.
   *   isEntering: Whether the popover is currently entering (for animations).
   *   isExiting: Whether the popover is currently exiting (for animations).
   */
  /** Hide the close button */
  isCloseHidden?: boolean;
  children: ReactAriaPopoverProps['children'];
};

/**
 * `Popover` is used to display helpful interactable content to the user.
 *
 * It's a modal dialog crossed with a tooltip.
 *
 * Built with React Aria's
 * [Popover](https://react-spectrum.adobe.com/react-aria/Popover.html) and
 * [Dialog](https://react-spectrum.adobe.com/react-aria/Dialog.html) components.
 */
export const Popover = ({
  children,
  className,
  color = 'dark',
  onOpenChange,
  title,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  isCloseHidden = false,
  ...otherProps
}: PopoverProps) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const { buttonProps } = useButton(
    { onPress: () => onOpenChange && onOpenChange(false) },
    ref
  );

  return (
    <ReactAriaPopover
      className={(renderProps) =>
        mergeClassNames([styles.popover, className], renderProps)
      }
      data-color={color}
      onOpenChange={onOpenChange}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      {...otherProps}
    >
      {(popoverProps) => (
        <Dialog
          className={styles.dialog}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        >
          <Text
            type="SmallPrint3"
            color={color === 'dark' ? 'GrayWarm1' : 'GrayWarm10'}
          >
            <div className={styles.popoverHeaderLayout}>
              <Text
                type="SmallPrint2"
                color={color === 'dark' ? 'BrandLavender' : 'UiPrimaryStronger'}
                tag="div"
              >
                {title}
              </Text>
              {!isCloseHidden && (
                <button
                  ref={ref}
                  {...buttonProps}
                  className={styles.closeButton}
                  type="button"
                  aria-label="close popover"
                >
                  <Icon
                    isBlock
                    name="formX"
                    size={16}
                    color={color === 'dark' ? 'GrayWarm1' : 'GrayWarm10'}
                  />
                </button>
              )}
            </div>
            <div className={styles.popoverContentScrollContainer}>
              <div className={styles.popoverContent} data-color={color}>
                {typeof children === 'function'
                  ? children(popoverProps)
                  : children}
              </div>
            </div>
          </Text>
        </Dialog>
      )}
    </ReactAriaPopover>
  );
};
